import { Flex, Spin } from 'antd';
import { CRX_SUPPORT_EMAIL } from 'app/config';
import { BaseButton } from 'components/button/button-base';
import { ErrorDisplay } from 'components/ErrorDisplay';
import { BaseHeader } from 'components/header';
import { Text } from 'components/mvp-typography';
import { PrintableRootPage } from 'components/page/page-2';
import useSearchParams from 'lib/hooks/use-searchParams';
import { PropsWithChildren } from 'react';
import { Link } from 'react-router-dom';
import { useIsTokenValid } from '../_utils/use-impact-pilot-mutations';

function Root(props: PropsWithChildren<any>) {
  return (
    <div>
      <BaseHeader headerStyle={{ background: 'none' }} />
      <PrintableRootPage>{props.children}</PrintableRootPage>
    </div>
  );
}

export function IntegrationImpactLayout(props: PropsWithChildren<any>) {
  const [params] = useSearchParams();

  const userToken: string | null = params.get('token');
  const locationToken: string | null = params.get('location');
  const validUserTokenQuery = useIsTokenValid(userToken);
  const validLocationToken = useIsTokenValid(locationToken);

  /**
   * Case 1: One or more of the tokens are missing
   */
  if (!userToken || !locationToken) {
    return (
      <Root>
        <Flex vertical gap={16}>
          <ErrorDisplay
            error={{
              message: `Missing required data from ImPACT. ${JSON.stringify({
                userToken,
                locationToken
              })}`
            }}
            title="Missing required data"
            description={
              <Text>
                Please screenshot this page and report the error to{' '}
                <a href={`mailto:${CRX_SUPPORT_EMAIL}`}>{CRX_SUPPORT_EMAIL}</a>
              </Text>
            }
          />

          <Link to={'/'}>
            <BaseButton>Home</BaseButton>
          </Link>
        </Flex>
      </Root>
    );
  }

  /**
   * Case 2: The queries are still loading
   */
  if (validUserTokenQuery.isLoading || validLocationToken.isLoading) {
    return (
      <Root>
        <Flex gap={16} align="center" justify="center">
          <Spin />
          Fetching details...
        </Flex>
      </Root>
    );
  }

  /**
   * Case 3: One or more of the token validations failed.
   *
   * This would be caused by a failure in the validation endpoint
   */
  if (validUserTokenQuery.isError || validLocationToken.isError) {
    return (
      <Root>
        <Flex vertical gap={16}>
          {validUserTokenQuery.isError && (
            <ErrorDisplay
              error={validUserTokenQuery.error as any}
              title="An error occured when validating information from ImPACT"
              description={
                <Text>
                  User token: {userToken} <br />
                  Please screenshot this page and report the error to{' '}
                  <a href={`mailto:${CRX_SUPPORT_EMAIL}`}>
                    {CRX_SUPPORT_EMAIL}
                  </a>
                </Text>
              }
            />
          )}
          {validUserTokenQuery.isError && (
            <ErrorDisplay
              error={validUserTokenQuery.error as any}
              title="An error occured when validating information from ImPACT"
              description={
                <Text>
                  Location token: {locationToken} <br />
                  Please screenshot this page and report the error to{' '}
                  <a href={`mailto:${CRX_SUPPORT_EMAIL}`}>
                    {CRX_SUPPORT_EMAIL}
                  </a>
                </Text>
              }
            />
          )}

          <Link to={'/'}>
            <BaseButton>Home</BaseButton>
          </Link>
        </Flex>
      </Root>
    );
  }

  /**
   * Case 4: One or more of the tokens failed validation
   */
  if (
    validUserTokenQuery.data.isValid === false ||
    validLocationToken.data.isValid === false
  ) {
    return (
      <Root>
        <Flex vertical gap={16}>
          {validUserTokenQuery.data.isValid === false && (
            <ErrorDisplay
              error={{
                message: validUserTokenQuery.data.status
              }}
              title="The provided user token is invalid"
              description={
                <Text>
                  User token: {userToken} <br />
                  Please screenshot this page and report the error to{' '}
                  <a href={`mailto:${CRX_SUPPORT_EMAIL}`}>
                    {CRX_SUPPORT_EMAIL}
                  </a>
                </Text>
              }
            />
          )}
          {validLocationToken.data.isValid === false && (
            <ErrorDisplay
              error={{
                message: validLocationToken.data.status
              }}
              title="The provided location token is invalid"
              description={
                <Text>
                  Location token: {locationToken} <br />
                  Please screenshot this page and report the error to{' '}
                  <a href={`mailto:${CRX_SUPPORT_EMAIL}`}>
                    {CRX_SUPPORT_EMAIL}
                  </a>
                </Text>
              }
            />
          )}

          <Link to={'/'}>
            <BaseButton>Home</BaseButton>
          </Link>
        </Flex>
      </Root>
    );
  }
  return props.children;
}
